import type { ReactNode } from 'react';
import { Building2, ExternalLink, GitBranch, Globe, Loader2, Mail, MapPin, Phone, RefreshCw, Users } from 'lucide-react';
import type { HubSpotQuoteData, HubSpotQuoteDataError } from '@/hooks/useHubSpotQuoteData';
import { hubspotRecordUrl } from '@/lib/hubspotContext';

export interface QuoteCustomerOverviewProps {
  data: HubSpotQuoteData;
  loaded: boolean;
  portalId?: string | null;
  dealId?: string | null;
  errors: HubSpotQuoteDataError[];
  /** Templates hangen nooit aan een deal; de melding legt dan uit waar de variabelen blijven. */
  isTemplate?: boolean;
  /** Haalt de gegevens opnieuw op uit HubSpot, bijvoorbeeld na het koppelen van een contactpersoon. */
  onRefresh?: () => void;
  refreshing?: boolean;
}

const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';

function websiteUrl(value: string) {
  if (!value) return undefined;
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(value) ? value : `https://${value}`);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function amountLabel(value: unknown, currency: unknown) {
  if (!text(value) || !Number.isFinite(Number(value))) return 'Niet ingevuld';
  const amount = Number(value);
  const options: Intl.NumberFormatOptions = { style: 'currency', currency: text(currency) || 'EUR' };
  try {
    return new Intl.NumberFormat('nl-NL', options).format(amount);
  } catch {
    return new Intl.NumberFormat('nl-NL', { ...options, currency: 'EUR' }).format(amount);
  }
}

function dateLabel(value: unknown) {
  const raw = text(value);
  if (!raw) return 'Niet ingevuld';
  const date = new Date(/^\d+$/.test(raw) ? Number(raw) : raw);
  return Number.isNaN(date.getTime()) ? 'Niet ingevuld' : new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(date);
}

function RecordLink({ portalId, id, type, label }: {
  portalId?: string | null;
  id?: string | null;
  type: '0-1' | '0-2' | '0-3';
  label: string;
}) {
  if (!portalId || !id) return null;
  return (
    <a className="workspace-customer-record-link"
      href={hubspotRecordUrl(portalId, type, id)}
      target="_blank" rel="noopener noreferrer" aria-label={`${label} openen in HubSpot`} title="Openen in HubSpot">
      <ExternalLink size={15} aria-hidden="true" />
    </a>
  );
}

function InfoLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return <div className="workspace-customer-info-line">{icon}<span>{children}</span></div>;
}

export default function QuoteCustomerOverview({ data, loaded, portalId, dealId, errors, isTemplate, onRefresh, refreshing }: QuoteCustomerOverviewProps) {
  if (!portalId || !dealId) {
    return (
      <div className="workspace-customer-overview" data-quote-customer-overview>
        <h3>Klant & deal</h3>
        <p className="workspace-customer-message">
          {isTemplate
            ? 'Een template hangt niet aan een deal. Variabelen zoals {{deal.name}} worden ingevuld zodra je er een offerte van maakt.'
            : 'Deze offerte is niet gekoppeld aan een HubSpot-deal. Klant- en contactgegevens verschijnen hier bij een gekoppelde offerte.'}
        </p>
      </div>
    );
  }
  if (!loaded) {
    return (
      <div className="workspace-customer-loading" data-quote-customer-overview role="status">
        <Loader2 size={18} className="animate-spin" aria-hidden="true" /> Klant- en dealgegevens ophalen…
      </div>
    );
  }

  const company = data.company;
  const cp = company?.properties;
  const contacts = data.contacts ?? [];
  const deal = data.deal;
  const dp = deal?.properties;
  const website = text(cp?.website) || text(cp?.domain);
  const websiteHref = websiteUrl(website);
  const address = [text(cp?.address), [text(cp?.zip), text(cp?.city)].filter(Boolean).join(' '), text(cp?.country)].filter(Boolean).join(', ');
  const phone = text(cp?.phone);
  const companyName = text(cp?.name) || text(cp?.domain) || 'Naam niet ingevuld';
  const dealUrl = hubspotRecordUrl(portalId, '0-3', deal?.id || dealId);

  return (
    <div className="workspace-customer-overview" data-quote-customer-overview>
      <div className="workspace-customer-actions">
        <a className="workspace-customer-deal-button" href={dealUrl} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={15} aria-hidden="true" /> Deal openen in HubSpot
        </a>
        {onRefresh && (
          <button type="button" className="workspace-customer-refresh" onClick={onRefresh} disabled={refreshing}
            aria-label="Gegevens opnieuw ophalen uit HubSpot" title="Opnieuw ophalen uit HubSpot">
            <RefreshCw size={15} aria-hidden="true" className={refreshing ? 'animate-spin' : undefined} />
          </button>
        )}
      </div>
      <p className="workspace-customer-intro">
        Gegevens bij deze offerte uit HubSpot. Pas ze aan in de deal; kom je terug in dit tabblad, dan worden ze hier en in de offerte bijgewerkt.
      </p>
      <section aria-labelledby="workspace-customer-company">
        <header className="workspace-customer-section-heading">
          <h3 id="workspace-customer-company"><Building2 size={17} aria-hidden="true" /> Klant</h3>
          <RecordLink portalId={portalId} id={company?.id} type="0-2" label={companyName} />
        </header>
        {company ? <>
          <h4>{companyName}</h4>
          {website && <InfoLine icon={<Globe size={14} aria-hidden="true" />}>
            {websiteHref ? <a href={websiteHref} target="_blank" rel="noopener noreferrer">{website.replace(/^https?:\/\//i, '').replace(/\/$/, '')}</a> : website}
          </InfoLine>}
          {address && <InfoLine icon={<MapPin size={14} aria-hidden="true" />}>{address}</InfoLine>}
          {phone && <InfoLine icon={<Phone size={14} aria-hidden="true" />}><a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a></InfoLine>}
          {!website && !address && !phone && <p className="workspace-customer-message">Geen verdere bedrijfsgegevens ingevuld.</p>}
        </> : <p className="workspace-customer-message">{errors.includes('company') ? 'Klantgegevens konden niet worden opgehaald.' : 'Geen bedrijf gekoppeld aan deze deal.'}</p>}
      </section>

      <section aria-labelledby="workspace-customer-contacts">
        <header className="workspace-customer-section-heading">
          <h3 id="workspace-customer-contacts"><Users size={17} aria-hidden="true" /> Contactpersonen <span>{contacts.length}</span></h3>
          <a className="workspace-customer-manage-link" href={dealUrl} target="_blank" rel="noopener noreferrer"
            title="Contactpersonen koppelen of ontkoppelen op de deal in HubSpot">
            Beheren
          </a>
        </header>
        {contacts.length ? <ul className="workspace-customer-contacts">
          {contacts.map((contact) => {
            const p = contact.properties;
            const email = text(p.email);
            const name = [text(p.firstname), text(p.lastname)].filter(Boolean).join(' ') || email || 'Naam niet ingevuld';
            const primary = ['hs_is_primary_contact', 'primary_contact', 'is_primary', 'primary'].some((key) => [true, 'true', 1, '1'].includes(p[key]));
            const phones = [...new Set([text(p.phone), text(p.mobilephone)].filter(Boolean))];
            return <li key={contact.id}>
              <div className="workspace-customer-contact-heading">
                <h4>{name}</h4>
                {primary && <span className="workspace-customer-primary">Primair</span>}
                <RecordLink portalId={portalId} id={contact.id} type="0-1" label={name} />
              </div>
              {text(p.jobtitle) && <p className="workspace-customer-role">{text(p.jobtitle)}</p>}
              {email && <InfoLine icon={<Mail size={14} aria-hidden="true" />}><a href={`mailto:${email}`}>{email}</a></InfoLine>}
              {phones.map((number) => <InfoLine key={number} icon={<Phone size={14} aria-hidden="true" />}><a href={`tel:${number.replace(/[^\d+]/g, '')}`}>{number}</a></InfoLine>)}
              {!email && !phones.length && <p className="workspace-customer-message">Geen e-mail of telefoonnummer ingevuld.</p>}
            </li>;
          })}
        </ul> : <p className="workspace-customer-message">{errors.includes('contacts') ? 'Contactpersonen konden niet worden opgehaald.' : 'Geen contactpersonen gekoppeld aan deze deal.'}</p>}
      </section>

      <section aria-labelledby="workspace-customer-deal">
        <header className="workspace-customer-section-heading">
          <h3 id="workspace-customer-deal"><GitBranch size={17} aria-hidden="true" /> Pipeline & deal</h3>
          <RecordLink portalId={portalId} id={deal?.id || dealId} type="0-3" label="Deal" />
        </header>
        {deal ? <>
          <h4>{text(dp?.dealname) || 'Dealnaam niet ingevuld'}</h4>
          <dl className="workspace-customer-deal-fields">
            <div><dt>Pipeline</dt><dd>{text(deal.pipelineInfo?.label) || (text(dp?.pipeline) ? 'Niet beschikbaar' : 'Niet ingevuld')}</dd></div>
            <div><dt>Dealfase</dt><dd>{text(deal.pipelineInfo?.stage?.label) || (text(dp?.dealstage) ? 'Niet beschikbaar' : 'Niet ingevuld')}</dd></div>
            <div><dt>Dealwaarde</dt><dd>{amountLabel(dp?.amount, dp?.deal_currency_code)}</dd></div>
            <div><dt>Verwachte sluitingsdatum</dt><dd>{dateLabel(dp?.closedate)}</dd></div>
          </dl>
          {text(dp?.hs_next_step) && <div className="workspace-customer-note"><h5>Volgende stap</h5><p>{text(dp?.hs_next_step)}</p></div>}
          {text(dp?.description) && <div className="workspace-customer-note"><h5>Dealomschrijving</h5><p>{text(dp?.description)}</p></div>}
        </> : <p className="workspace-customer-message">{errors.includes('deal') ? 'Dealgegevens konden niet worden opgehaald.' : 'Geen dealgegevens beschikbaar.'}</p>}
      </section>
    </div>
  );
}
